import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { agenda, agendaDotColor, agendaFonts } from '../domain/agendaTheme';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { AppointmentResourceKind } from '../domain/types';

const KIND_LABEL: Record<AppointmentResourceKind, string> = {
  person: 'Operario',
  bay: 'Lift / box',
  equipment: 'Equipo',
};

type Props = {
  displayName: string;
  color: string | null | undefined;
  employeeName: string | null;
  resourceKind: AppointmentResourceKind;
  /** Compact week summary, e.g. "Lun–Vie 09:00–18:00". */
  hoursSummary: string;
  /** "3 servicios" | "Todos los servicios" | null while loading. */
  servicesText: string | null;
  index: number;
  isActive: boolean;
  isLast: boolean;
  onPress: () => void;
};

export function AgendaSummaryCard({
  displayName,
  color,
  employeeName,
  resourceKind,
  hoursSummary,
  servicesText,
  index,
  isActive,
  isLast,
  onPress,
}: Props) {
  const dotColor = agendaDotColor(color, index);
  const needsPerson = resourceKind === 'person' && !employeeName;
  const who = employeeName ?? (resourceKind === 'person' ? 'Asignar persona' : KIND_LABEL[resourceKind]);

  const metaParts = isActive
    ? [who, hoursSummary, servicesText].filter(Boolean).join(' · ')
    : 'No entra en turnos nuevos ni en reservas';

  return (
    <TouchableOpacity
      style={[styles.row, !isLast && styles.rowBorder]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={`Configurar agenda ${displayName}${isActive ? '' : ' (inactiva)'}`}
    >
      <View style={[styles.dot, { backgroundColor: isActive ? dotColor : agenda.ink3 }]} />
      <View style={styles.main}>
        <Text style={[styles.title, !isActive && styles.titleInactive]} numberOfLines={1}>
          {displayName}
        </Text>
        <View style={styles.metaRow}>
          {needsPerson && isActive ? (
            <>
              <Text style={[styles.meta, styles.metaBrand]}>Asignar persona</Text>
              <Text style={styles.meta} numberOfLines={1}>
                {` · ${[hoursSummary, servicesText].filter(Boolean).join(' · ')}`}
              </Text>
            </>
          ) : (
            <Text style={styles.meta} numberOfLines={2}>
              {metaParts}
            </Text>
          )}
        </View>
      </View>
      {!isActive ? <Text style={styles.pillInactive}>Inactiva</Text> : null}
      {isActive ? <Ionicons name="chevron-forward-outline" size={18} color={agenda.ink3} /> : null}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  rowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  main: { flex: 1, minWidth: 0 },
  title: {
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  titleInactive: { color: agenda.ink2 },
  metaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 1 },
  meta: {
    fontSize: 12,
    color: agenda.ink3,
    fontFamily: agendaFonts.sans,
    flexShrink: 1,
  },
  metaBrand: { color: agenda.brand, fontWeight: '700', flexShrink: 0 },
  pillInactive: {
    fontSize: 10.5,
    fontWeight: '700',
    color: agenda.ink3,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    overflow: 'hidden',
    fontFamily: agendaFonts.sans,
  },
});
